import { css } from "@emotion/react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogContent,
  IconButton,
  Theme,
  useTheme,
} from "@mui/material";
import { MouseEventHandler } from "react";
import { match } from "ts-pattern";

import Loading from "~/components/me/bento/Loading";
import RetriableError from "~/components/me/bento/RetriableError";
import ContentBodyLayout from "~/components/me/bento/content/ContentBodyLayout";
import DialogBody from "~/components/me/bento/content/quest-log/DialogBody";
import { filterActiveQuest } from "~/components/me/bento/content/quest-log/lib";
import { useMeQuestContent } from "~/components/me/bento/lib";
import { useOpenContentModal } from "~/components/me/hooks";
import { ContentName } from "~/components/me/lib";
import { engFont } from "~/lib/client/fonts";
import { SP_MODE_WIDTH } from "~/lib/client/hooks/useIsSpMode";
import {
  MeQuestContentQuery,
  Sdk,
  UserQuestProgressStatus,
} from "~/lib/graphql/client";

const contentName: ContentName = "QuestLog";

const QuestLog = ({ client }: { client: Sdk }) => {
  const result = useMeQuestContent(client);
  const theme = useTheme();

  const { openedContent, openContentModal, closeContentModal } =
    useOpenContentModal();
  const isOpen = openedContent === contentName && result.status === "success";
  const openModal = () => {
    if (result.status !== "success") return;
    openContentModal(contentName);
  };

  const closeModal = () => closeContentModal();

  return (
    <>
      {match(result)
        .with({ status: "pending" }, () => <Loading />)
        .with({ status: "error" }, (matched) => (
          <RetriableError reload={matched.refetch} />
        ))
        .with({ status: "success" }, (matched) => (
          <Body content={matched.data} onClick={openModal} />
        ))
        .exhaustive()}
      {result.status == "success" && (
        <Dialog
          open={isOpen}
          onClose={closeModal}
          fullWidth
          PaperProps={{
            sx: {
              width: "100%",
              height: "80vh",
              m: 1,
              backgroundColor: theme.palette.background.paper,
            },
          }}
          maxWidth={"md"}
        >
          <DialogContent>
            <IconButton onClick={closeModal}>
              <CloseIcon sx={{ color: theme.palette.text.primary }} />
            </IconButton>
            <DialogBody content={result.data} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

const Body = ({
  content,
  onClick,
}: {
  content: MeQuestContentQuery;
  onClick: MouseEventHandler;
}) => {
  const theme = useTheme();
  const activeQuest = content.me.userQuestProgresses.filter(filterActiveQuest);
  const completeQuest = content.me.userQuestProgresses.filter(
    (p) => p.status == UserQuestProgressStatus.Verified
  );
  return (
    <ContentBodyLayout onClick={onClick} label={"Quest Log"}>
      <div css={rootStyle}>
        <div css={activeQuestStyle}>
          <p css={contentLabelStyle} className={engFont.className}>
            In Progress
          </p>
          <p
            css={questLengthStyle(theme, "primary")}
            className={engFont.className}
          >
            {activeQuest.length}
          </p>
        </div>
        <div css={completeQuestStyle(theme)}>
          <p css={contentLabelStyle}>Completed</p>
          <p
            css={questLengthStyle(theme, "secondary")}
            className={engFont.className}
          >
            {completeQuest.length}
          </p>
        </div>
      </div>
    </ContentBodyLayout>
  );
};

const rootStyle = css`
  display: flex;
  flex-direction: row;

  width: 100%;
  height: 108%;
  padding: 1rem 2rem 0;
  margin-top: -1rem;
  margin-bottom: 1rem;
`;

const activeQuestStyle = css`
  width: 100%;
  height: 100%;
  flex: 1;
`;

const completeQuestStyle = (theme: Theme) => css`
  width: 100%;
  height: 100%;
  flex: 1;
  border-left: 1px solid ${theme.palette.divider};
  padding-left: 1rem;
`;

const contentLabelStyle = css`
  font-size: 12px;
  font-weight: 600;
`;

const questLengthStyle = (theme: Theme, color: "primary" | "secondary") => css`
  font-size: 80px;
  font-weight: 700;
  color: ${theme.palette[color].main};
  margin: 0;
  text-align: center;
  padding-top: -1rem;

  @media (max-width: ${SP_MODE_WIDTH}px) {
    font-size: 40px;
  }
`;

export default QuestLog;
